/**
 * The history's day separators (spec 2026-09-24 §1): rows grouped by the
 * local calendar day they settled on, labelled "Today", "Yesterday", or
 * "Tue, 22 Sep" (with the year when it is not this one). Everything reads
 * the `now` it is handed, the open gesture's stamp, so render stays pure.
 */
import type { ComposerHistoryEntry } from "@/stores/composerHistoryStore";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const dayKey = (d: Date): string =>
  `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

export function dayLabel(ts: number, now: number): string {
  const day = new Date(ts);
  const today = new Date(now);
  if (dayKey(day) === dayKey(today)) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dayKey(day) === dayKey(yesterday)) return "Yesterday";
  const label = `${WEEKDAYS[day.getDay()]}, ${day.getDate()} ${MONTHS[day.getMonth()]}`;
  return day.getFullYear() === today.getFullYear()
    ? label
    : `${label} ${day.getFullYear()}`;
}

export interface HistoryDay {
  /** The calendar day itself, so a group keeps its identity when an older
   *  page lands rows above its first one (its rows never remount). Unique
   *  within the list: a day that comes back later gets a run number. */
  key: string;
  label: string;
  rows: ComposerHistoryEntry[];
}

/** Rows in chat order (oldest first) in, one group per run of rows on the same day out. */
export function historyDays(
  rows: readonly ComposerHistoryEntry[],
  now: number
): HistoryDay[] {
  const days: HistoryDay[] = [];
  const runs = new Map<string, number>();
  let lastDay = "";
  for (const row of rows) {
    const day = dayKey(new Date(row.ts));
    if (days.length > 0 && day === lastDay)
      days[days.length - 1].rows.push(row);
    else {
      const run = runs.get(day) ?? 0;
      runs.set(day, run + 1);
      days.push({
        key: run === 0 ? day : `${day}#${run}`,
        label: dayLabel(row.ts, now),
        rows: [row],
      });
    }
    lastDay = day;
  }
  return days;
}
